import React, { useContext, useEffect, useRef, useState } from "react";
import { useNavigate, NavLink, Outlet } from "react-router-dom";
import {
  LayoutDashboard,
  FileText,
  Car,
  Users,
  BarChart2,
  Leaf,
  Download,
  Settings,
  LogOut,
  Menu,
  Bell,
  ChevronDown,
  Calendar,
  User,
} from "lucide-react";
import { AuthContext } from "../AuthContext";

// ---------- Small building blocks ----------

function NavItem({ icon: Icon, label, to, end }) {
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) =>
        `flex items-center gap-3 px-4 py-2.5 rounded-lg cursor-pointer transition-colors text-sm ${
          isActive
            ? "bg-white text-emerald-800 font-semibold"
            : "text-emerald-50/80 hover:bg-emerald-800/60"
        }`
      }
    >
      <Icon size={18} />
      <span>{label}</span>
    </NavLink>
  );
}

// ---------- Main Layout Component ----------

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { user, setUser } = useContext(AuthContext);

  const [profileOpen, setProfileOpen] = useState(false);
  const profileRef = useRef(null);

  // Close profile dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (profileRef.current && !profileRef.current.contains(e.target)) {
        setProfileOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleLogout = () => {
    setUser(null);
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    navigate("/login");
  };

  return (
    <div className="h-screen w-full flex bg-gray-50 font-sans text-gray-900 overflow-hidden">
      {/* Sidebar - fixed, does not scroll with content */}
      <aside className="w-64 h-screen bg-[#0E3B2E] flex flex-col shrink-0 overflow-y-auto">
        <div className="flex flex-col items-center text-center px-6 pt-8 pb-6 border-b border-emerald-800/60">
          <div className="w-16 h-16 rounded-full bg-white flex items-center justify-center mb-3">
            <Leaf className="text-emerald-700" size={28} />
          </div>
          <h1 className="text-white font-bold text-lg leading-tight">PURE PRAKRUTI</h1>
          <p className="text-emerald-200/70 text-xs mt-1">Green Certificate System</p>
        </div>

        <nav className="flex-1 px-3 py-4 space-y-1">
          <NavItem icon={LayoutDashboard} label="Dashboard" to="/AdminDashboard" end />
          <NavItem icon={FileText} label="Green Certificates" to="/AdminDashboard/certificates" />
          <NavItem icon={Car} label="Vehicles" to="/AdminDashboard/vehicles" />
          <NavItem icon={Users} label="Users" to="/AdminDashboard/users" />
          <NavItem icon={BarChart2} label="Reports" to="/AdminDashboard/reports" />
          <NavItem icon={Leaf} label="Environmental Impact" to="/AdminDashboard/impact" />
          <NavItem icon={Download} label="Download Center" to="/AdminDashboard/downloads" />
          <NavItem icon={Settings} label="Settings" to="/AdminDashboard/settings" />
        </nav>

        <div className="mx-3 mb-4 bg-emerald-800/40 rounded-2xl p-4 text-emerald-50">
          <p className="text-sm font-semibold leading-snug mb-3">
            Together we can make our planet greener
          </p>
          <div className="w-full h-20 rounded-xl bg-emerald-900/40 flex items-center justify-center">
            <Leaf size={32} className="text-emerald-300" />
          </div>
        </div>

        <div className="px-6 pb-6">
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 text-emerald-100 text-sm hover:text-white"
          >
            <LogOut size={16} />
            Logout
          </button>
        </div>
      </aside>

      {/* Main area: fixed-height column — header stays put, content scrolls */}
      <main className="flex-1 h-screen flex flex-col overflow-hidden">
        {/* Header - fixed, does not scroll */}
        <div className="flex items-center justify-between px-8 py-6 shrink-0 bg-gray-50 border-b border-gray-100">
          <div className="flex items-center gap-4">
            <Menu className="text-gray-500" size={22} />
            <div>
              <h2 className="text-xl font-bold text-gray-900">Dashboard</h2>
              <p className="text-sm text-gray-500">
                Welcome back, {user?.userName || "Admin"} 👋
              </p>
            </div>
          </div>

          <div className="flex items-center gap-4">
            <button className="flex items-center gap-2 border border-gray-200 rounded-xl px-4 py-2 text-sm text-gray-600 bg-white">
              <Calendar size={16} />
              Dec 20, 2025 - Dec 26, 2025
              <ChevronDown size={14} />
            </button>
            <div className="relative">
              <Bell className="text-gray-500" size={20} />
              <span className="absolute -top-1 -right-1 bg-emerald-600 text-white text-[10px] rounded-full w-4 h-4 flex items-center justify-center">
                5
              </span>
            </div>

            {/* Profile dropdown */}
            <div className="relative" ref={profileRef}>
              <button
                onClick={() => setProfileOpen((prev) => !prev)}
                className="flex items-center gap-2"
              >
                {user?.avatar ? (
                  <img
                    src={user.avatar}
                    alt="Admin avatar"
                    className="w-9 h-9 rounded-full object-cover"
                  />
                ) : (
                  <div className="w-9 h-9 rounded-full bg-emerald-100 flex items-center justify-center">
                    <User size={18} className="text-emerald-700" />
                  </div>
                )}
                <div className="text-sm text-left">
                  <p className="font-semibold leading-tight">{user?.userName || "Admin"}</p>
                  <p className="text-gray-400 text-xs leading-tight">
                    {user?.role || "Super Admin"}
                  </p>
                </div>
                <ChevronDown size={14} className="text-gray-400" />
              </button>

              {profileOpen && (
                <div className="absolute right-0 mt-2 w-44 bg-white border border-gray-100 rounded-xl shadow-lg py-2 z-10">
                  <button
                    onClick={() => navigate("/AdminDashboard/settings")}
                    className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-600 hover:bg-gray-50"
                  >
                    <Settings size={15} /> Settings
                  </button>
                  <button
                    onClick={handleLogout}
                    className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-500 hover:bg-red-50"
                  >
                    <LogOut size={15} /> Logout
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Scrollable content area */}
        <div className="flex-1 overflow-y-auto px-8 py-6">
          {/* Routed page content renders here */}
          <Outlet />

          {/* Footer */}
          <div className="flex items-center justify-between text-xs text-gray-400 mt-6 pb-2">
            <span>© 2025 Pure Prakruti Green Certificate System. All rights reserved.</span>
            <span className="flex items-center gap-1">
              Every Certificate Counts. Every Act Matters. <Leaf size={12} className="text-emerald-500" />
            </span>
          </div>
        </div>
      </main>
    </div>
  );
}

// import React, { useContext, useEffect, useRef, useState } from "react";
// import { useNavigate, NavLink, Outlet } from "react-router-dom";
// import {
//   LayoutDashboard,
//   FileText,
//   Car,
//   Users,
//   BarChart2,
//   Leaf,
//   Download,
//   Settings,
//   LogOut,
//   Menu,
//   Bell,
//   ChevronDown,
//   Calendar,
//   User,
// } from "lucide-react";
// import { AuthContext } from "../AuthContext";

// // ---------- Small building blocks ----------

// function NavItem({ icon: Icon, label, to, end }) {
//   return (
//     <NavLink
//       to={to}
//       end={end}
//       className={({ isActive }) =>
//         `flex items-center gap-3 px-4 py-2.5 rounded-lg cursor-pointer transition-colors text-sm ${
//           isActive
//             ? "bg-white text-emerald-800 font-semibold"
//             : "text-emerald-50/80 hover:bg-emerald-800/60"
//         }`
//       }
//     >
//       <Icon size={18} />
//       <span>{label}</span>
//     </NavLink>
//   );
// }

// // ---------- Main Layout Component ----------

// export default function AdminDashboard() {
//   const navigate = useNavigate();
//   const { user, setUser } = useContext(AuthContext);

//   const [profileOpen, setProfileOpen] = useState(false);
//   const profileRef = useRef(null);

//   // Close profile dropdown on outside click
//   useEffect(() => {
//     const handleClickOutside = (e) => {
//       if (profileRef.current && !profileRef.current.contains(e.target)) {
//         setProfileOpen(false);
//       }
//     };
//     document.addEventListener("mousedown", handleClickOutside);
//     return () => document.removeEventListener("mousedown", handleClickOutside);
//   }, []);

//   const handleLogout = () => {
//     setUser(null);
//     localStorage.removeItem("token");
//     localStorage.removeItem("user");
//     navigate("/login");
//   };

//   return (
//     <div className="min-h-screen w-full flex bg-gray-50 font-sans text-gray-900">
//       {/* Sidebar */}
//       <aside className="w-64 bg-[#0E3B2E] flex flex-col shrink-0">
//         <div className="flex flex-col items-center text-center px-6 pt-8 pb-6 border-b border-emerald-800/60">
//           <div className="w-16 h-16 rounded-full bg-white flex items-center justify-center mb-3">
//             <Leaf className="text-emerald-700" size={28} />
//           </div>
//           <h1 className="text-white font-bold text-lg leading-tight">PURE PRAKRUTI</h1>
//           <p className="text-emerald-200/70 text-xs mt-1">Green Certificate System</p>
//         </div>

//         <nav className="flex-1 px-3 py-4 space-y-1">
//           <NavItem icon={LayoutDashboard} label="Dashboard" to="/AdminDashboard" end />
//           <NavItem icon={FileText} label="Green Certificates" to="/AdminDashboard/certificates" />
//           <NavItem icon={Car} label="Vehicles" to="/AdminDashboard/vehicles" />
//           <NavItem icon={Users} label="Users" to="/AdminDashboard/users" />
//           <NavItem icon={BarChart2} label="Reports" to="/AdminDashboard/reports" />
//           <NavItem icon={Leaf} label="Environmental Impact" to="/AdminDashboard/impact" />
//           <NavItem icon={Download} label="Download Center" to="/AdminDashboard/downloads" />
//           <NavItem icon={Settings} label="Settings" to="/AdminDashboard/settings" />
//         </nav>

//         <div className="mx-3 mb-4 bg-emerald-800/40 rounded-2xl p-4 text-emerald-50">
//           <p className="text-sm font-semibold leading-snug mb-3">
//             Together we can make our planet greener
//           </p>
//           <div className="w-full h-20 rounded-xl bg-emerald-900/40 flex items-center justify-center">
//             <Leaf size={32} className="text-emerald-300" />
//           </div>
//         </div>

//         <div className="px-6 pb-6">
//           <button
//             onClick={handleLogout}
//             className="flex items-center gap-2 text-emerald-100 text-sm hover:text-white"
//           >
//             <LogOut size={16} />
//             Logout
//           </button>
//         </div>
//       </aside>

//       {/* Main content */}
//       <main className="flex-1 px-8 py-6 overflow-y-auto">
//         {/* Header */}
//         <div className="flex items-center justify-between mb-6">
//           <div className="flex items-center gap-4">
//             <Menu className="text-gray-500" size={22} />
//             <div>
//               <h2 className="text-xl font-bold text-gray-900">Dashboard</h2>
//               <p className="text-sm text-gray-500">
//                 Welcome back, {user?.userName || "Admin"} 👋
//               </p>
//             </div>
//           </div>

//           <div className="flex items-center gap-4">
//             <button className="flex items-center gap-2 border border-gray-200 rounded-xl px-4 py-2 text-sm text-gray-600 bg-white">
//               <Calendar size={16} />
//               Dec 20, 2025 - Dec 26, 2025
//               <ChevronDown size={14} />
//             </button>
//             <div className="relative">
//               <Bell className="text-gray-500" size={20} />
//               <span className="absolute -top-1 -right-1 bg-emerald-600 text-white text-[10px] rounded-full w-4 h-4 flex items-center justify-center">
//                 5
//               </span>
//             </div>

//             {/* Profile dropdown */}
//             <div className="relative" ref={profileRef}>
//               <button
//                 onClick={() => setProfileOpen((prev) => !prev)}
//                 className="flex items-center gap-2"
//               >
//                 {user?.avatar ? (
//                   <img
//                     src={user.avatar}
//                     alt="Admin avatar"
//                     className="w-9 h-9 rounded-full object-cover"
//                   />
//                 ) : (
//                   <div className="w-9 h-9 rounded-full bg-emerald-100 flex items-center justify-center">
//                     <User size={18} className="text-emerald-700" />
//                   </div>
//                 )}
//                 <div className="text-sm text-left">
//                   <p className="font-semibold leading-tight">{user?.userName || "Admin"}</p>
//                   <p className="text-gray-400 text-xs leading-tight">
//                     {user?.role || "Super Admin"}
//                   </p>
//                 </div>
//                 <ChevronDown size={14} className="text-gray-400" />
//               </button>

//               {profileOpen && (
//                 <div className="absolute right-0 mt-2 w-44 bg-white border border-gray-100 rounded-xl shadow-lg py-2 z-10">
//                   <button
//                     onClick={() => navigate("/AdminDashboard/settings")}
//                     className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-600 hover:bg-gray-50"
//                   >
//                     <Settings size={15} /> Settings
//                   </button>
//                   <button
//                     onClick={handleLogout}
//                     className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-500 hover:bg-red-50"
//                   >
//                     <LogOut size={15} /> Logout
//                   </button>
//                 </div>
//               )}
//             </div>
//           </div>
//         </div>

//         {/* Routed page content renders here */}
//         <Outlet />

//         {/* Footer */}
//         <div className="flex items-center justify-between text-xs text-gray-400 mt-6 pb-2">
//           <span>© 2025 Pure Prakruti Green Certificate System. All rights reserved.</span>
//           <span className="flex items-center gap-1">
//             Every Certificate Counts. Every Act Matters. <Leaf size={12} className="text-emerald-500" />
//           </span>
//         </div>
//       </main>
//     </div>
//   );
// }

// import React, { useState, useEffect, useContext, useRef } from "react";
// import { useNavigate } from "react-router-dom";
// import {
//   AreaChart,
//   Area,
//   XAxis,
//   YAxis,
//   CartesianGrid,
//   Tooltip,
//   ResponsiveContainer,
//   PieChart,
//   Pie,
//   Cell,
// } from "recharts";
// import {
//   LayoutDashboard,
//   FileText,
//   Car,
//   Users,
//   BarChart2,
//   Leaf,
//   Download,
//   Settings,
//   LogOut,
//   Menu,
//   Bell,
//   ChevronDown,
//   Calendar,
//   Bike,
//   Truck,
//   MoreHorizontal,
//   Eye,
//   MoreVertical,
//   TreePine,
//   Cloud,
//   User,
// } from "lucide-react";
// import { AuthContext } from "../AuthContext";
// import API_BASE_URL from "../api"; // ✅ same pattern as UserDashBoard.jsx

// // ---------- Config ----------

// const API_BASE = `${API_BASE_URL}/api/inputHistory`;

// // ---------- Mock data (kept only where no API exists yet) ----------

// // No endpoint yet for fuel-type/vehicle-category split — replace when available
// const vehicleCategoryData = [
//   { name: "Cars", value: 45, color: "#2E7D32" },
//   { name: "Bikes", value: 30, color: "#F5A623" },
//   { name: "Trucks", value: 15, color: "#1E6FD9" },
//   { name: "Others", value: 10, color: "#9AA0A6" },
// ];

// // ---------- Small building blocks ----------

// function NavItem({ icon: Icon, label, active }) {
//   return (
//     <div
//       className={`flex items-center gap-3 px-4 py-2.5 rounded-lg cursor-pointer transition-colors text-sm ${
//         active
//           ? "bg-white text-emerald-800 font-semibold"
//           : "text-emerald-50/80 hover:bg-emerald-800/60"
//       }`}
//     >
//       <Icon size={18} />
//       <span>{label}</span>
//     </div>
//   );
// }

// function StatCard({ label, value, unit, change, icon, iconBg, loading }) {
//   return (
//     <div className="bg-white rounded-2xl border border-gray-100 p-5 flex flex-col gap-2 shadow-sm">
//       <div className="flex items-start justify-between">
//         <span className="text-sm text-gray-500 flex items-center gap-1">
//           {label}
//           <span className="text-gray-300 text-xs">ⓘ</span>
//         </span>
//         <div
//           className="w-10 h-10 rounded-full flex items-center justify-center shrink-0"
//           style={{ backgroundColor: iconBg }}
//         >
//           {icon}
//         </div>
//       </div>
//       <div className="flex items-baseline gap-2">
//         {loading ? (
//           <span className="h-7 w-24 bg-gray-100 rounded animate-pulse" />
//         ) : (
//           <span className="text-2xl font-bold text-gray-900">{value}</span>
//         )}
//         {unit && !loading && <span className="text-sm text-gray-400">{unit}</span>}
//       </div>
//       {change && (
//         <span className="text-xs font-medium text-emerald-600">
//           ↑ {change} vs last {label.toLowerCase().includes("today") ? "yesterday" : "month"}
//         </span>
//       )}
//     </div>
//   );
// }

// function StatusPill({ status }) {
//   const isVerified = status === "Verified";
//   return (
//     <span
//       className={`inline-flex items-center gap-1 text-xs font-medium ${
//         isVerified ? "text-emerald-600" : "text-amber-500"
//       }`}
//     >
//       <span className="text-[8px]">●</span>
//       {status}
//     </span>
//   );
// }

// function CustomTooltip({ active, payload, label }) {
//   if (active && payload && payload.length) {
//     return (
//       <div className="bg-white border border-gray-100 shadow-lg rounded-xl px-4 py-3">
//         <p className="text-xs text-gray-400 mb-1">{label} {new Date().getFullYear()}</p>
//         <p className="text-sm font-semibold text-emerald-700">
//           {payload[0].value.toLocaleString()} Certificates
//         </p>
//       </div>
//     );
//   }
//   return null;
// }

// // ---------- Main Component ----------

// export default function AdminDashboard() {
//   const [range] = useState("This Year");
//   const navigate = useNavigate();

//   // ✅ Same AuthContext pattern used in UserDashBoard.jsx
//   const { user, setUser } = useContext(AuthContext);

//   const [profileOpen, setProfileOpen] = useState(false);
//   const profileRef = useRef(null);

//   // ---------- Live dashboard stats ----------
//   const [stats, setStats] = useState({
//     totalCertificates: null,
//     totalVehicles: null,
//     totalCarbonFootprint: null,
//   });
//   const [statsLoading, setStatsLoading] = useState(true);
//   const [statsError, setStatsError] = useState(null);

//   const [trendData, setTrendData] = useState([]);
//   const [trendLoading, setTrendLoading] = useState(true);

//   const [recentCertificates, setRecentCertificates] = useState([]);
//   const [recentLoading, setRecentLoading] = useState(true);

//   useEffect(() => {
//     const fetchStats = async () => {
//       setStatsLoading(true);
//       setStatsError(null);
//       try {
//         const [certRes, vehicleRes, carbonRes] = await Promise.all([
//           fetch(`${API_BASE}/totalCertificateGenerated`).then((r) => r.json()),
//           fetch(`${API_BASE}/totalVehicleRegistered`).then((r) => r.json()),
//           fetch(`${API_BASE}/totalCarbonFootprintGenerated`).then((r) => r.json()),
//         ]);

//         setStats({
//           totalCertificates: certRes?.totalCertificate ?? 0,
//           totalVehicles: vehicleRes?.totalVehicleRegistered ?? 0,
//           totalCarbonFootprint: carbonRes?.totalCarbonFootprintGenerated ?? 0,
//         });
//       } catch (err) {
//         console.error("Failed to load dashboard stats:", err);
//         setStatsError("Couldn't load live stats. Showing last known values.");
//       } finally {
//         setStatsLoading(false);
//       }
//     };

//     const fetchTrend = async () => {
//       setTrendLoading(true);
//       try {
//         const res = await fetch(`${API_BASE}/monthlyCertificateTrend`).then((r) => r.json());
//         setTrendData(res?.trend || []);
//       } catch (err) {
//         console.error("Failed to load certificate trend:", err);
//       } finally {
//         setTrendLoading(false);
//       }
//     };

//     const fetchRecent = async () => {
//       setRecentLoading(true);
//       try {
//         const res = await fetch(`${API_BASE}/recentCertificates`).then((r) => r.json());
//         setRecentCertificates(res?.recentCertificates || []);
//       } catch (err) {
//         console.error("Failed to load recent certificates:", err);
//       } finally {
//         setRecentLoading(false);
//       }
//     };

//     fetchStats();
//     fetchTrend();
//     fetchRecent();
//   }, []);

//   // Close profile dropdown on outside click
//   useEffect(() => {
//     const handleClickOutside = (e) => {
//       if (profileRef.current && !profileRef.current.contains(e.target)) {
//         setProfileOpen(false);
//       }
//     };
//     document.addEventListener("mousedown", handleClickOutside);
//     return () => document.removeEventListener("mousedown", handleClickOutside);
//   }, []);

//   const handleLogout = () => {
//     // Clear auth state the same way it's set on login
//     setUser(null);
//     localStorage.removeItem("token");
//     localStorage.removeItem("user");
//     navigate("/login");
//   };

//   const formattedCarbon =
//     stats.totalCarbonFootprint !== null
//       ? Number(stats.totalCarbonFootprint).toLocaleString(undefined, {
//           maximumFractionDigits: 2,
//         })
//       : "—";

//   return (
//     <div className="min-h-screen w-full flex bg-gray-50 font-sans text-gray-900">
//       {/* Sidebar */}
//       <aside className="w-64 bg-[#0E3B2E] flex flex-col shrink-0">
//         <div className="flex flex-col items-center text-center px-6 pt-8 pb-6 border-b border-emerald-800/60">
//           <div className="w-16 h-16 rounded-full bg-white flex items-center justify-center mb-3">
//             <Leaf className="text-emerald-700" size={28} />
//           </div>
//           <h1 className="text-white font-bold text-lg leading-tight">PURE PRAKRUTI</h1>
//           <p className="text-emerald-200/70 text-xs mt-1">Green Certificate System</p>
//         </div>

//         <nav className="flex-1 px-3 py-4 space-y-1">
//           <NavItem icon={LayoutDashboard} label="Dashboard" active />
//           <NavItem icon={FileText} label="Green Certificates" />
//           <NavItem icon={Car} label="Vehicles" />
//           <NavItem icon={Users} label="Users" />
//           <NavItem icon={BarChart2} label="Reports" />
//           <NavItem icon={Leaf} label="Environmental Impact" />
//           <NavItem icon={Download} label="Download Center" />
//           <NavItem icon={Settings} label="Settings" />
//         </nav>

//         <div className="mx-3 mb-4 bg-emerald-800/40 rounded-2xl p-4 text-emerald-50">
//           <p className="text-sm font-semibold leading-snug mb-3">
//             Together we can make our planet greener
//           </p>
//           <div className="w-full h-20 rounded-xl bg-emerald-900/40 flex items-center justify-center">
//             <Leaf size={32} className="text-emerald-300" />
//           </div>
//         </div>

//         <div className="px-6 pb-6">
//           <button
//             onClick={handleLogout}
//             className="flex items-center gap-2 text-emerald-100 text-sm hover:text-white"
//           >
//             <LogOut size={16} />
//             Logout
//           </button>
//         </div>
//       </aside>

//       {/* Main content */}
//       <main className="flex-1 px-8 py-6 overflow-y-auto">
//         {/* Header */}
//         <div className="flex items-center justify-between mb-6">
//           <div className="flex items-center gap-4">
//             <Menu className="text-gray-500" size={22} />
//             <div>
//               <h2 className="text-xl font-bold text-gray-900">Dashboard</h2>
//               <p className="text-sm text-gray-500">
//                 Welcome back, {user?.userName || "Admin"} 👋
//               </p>
//             </div>
//           </div>

//           <div className="flex items-center gap-4">
//             <button className="flex items-center gap-2 border border-gray-200 rounded-xl px-4 py-2 text-sm text-gray-600 bg-white">
//               <Calendar size={16} />
//               Dec 20, 2025 - Dec 26, 2025
//               <ChevronDown size={14} />
//             </button>
//             <div className="relative">
//               <Bell className="text-gray-500" size={20} />
//               <span className="absolute -top-1 -right-1 bg-emerald-600 text-white text-[10px] rounded-full w-4 h-4 flex items-center justify-center">
//                 5
//               </span>
//             </div>

//             {/* Profile dropdown */}
//             <div className="relative" ref={profileRef}>
//               <button
//                 onClick={() => setProfileOpen((prev) => !prev)}
//                 className="flex items-center gap-2"
//               >
//                 {user?.avatar ? (
//                   <img
//                     src={user.avatar}
//                     alt="Admin avatar"
//                     className="w-9 h-9 rounded-full object-cover"
//                   />
//                 ) : (
//                   <div className="w-9 h-9 rounded-full bg-emerald-100 flex items-center justify-center">
//                     <User size={18} className="text-emerald-700" />
//                   </div>
//                 )}
//                 <div className="text-sm text-left">
//                   <p className="font-semibold leading-tight">{user?.userName || "Admin"}</p>
//                   <p className="text-gray-400 text-xs leading-tight">
//                     {user?.role || "Super Admin"}
//                   </p>
//                 </div>
//                 <ChevronDown size={14} className="text-gray-400" />
//               </button>

//               {profileOpen && (
//                 <div className="absolute right-0 mt-2 w-44 bg-white border border-gray-100 rounded-xl shadow-lg py-2 z-10">
//                   <button
//                     onClick={() => navigate("/settings")}
//                     className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-600 hover:bg-gray-50"
//                   >
//                     <Settings size={15} /> Settings
//                   </button>
//                   <button
//                     onClick={handleLogout}
//                     className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-500 hover:bg-red-50"
//                   >
//                     <LogOut size={15} /> Logout
//                   </button>
//                 </div>
//               )}
//             </div>
//           </div>
//         </div>

//         {statsError && (
//           <div className="mb-4 text-xs text-amber-600 bg-amber-50 border border-amber-100 rounded-lg px-4 py-2">
//             {statsError}
//           </div>
//         )}

//         {/* Stat cards — now driven by live API data */}
//         <div className="grid grid-cols-3 gap-5 mb-6">
//           <StatCard
//             label="Total Green Certificates"
//             value={stats.totalCertificates?.toLocaleString() ?? "—"}
//             loading={statsLoading}
//             iconBg="#E8F5E9"
//             icon={<FileText size={18} className="text-emerald-700" />}
//           />
//           <StatCard
//             label="Total CO₂ Saved"
//             value={formattedCarbon}
//             unit="Kg"
//             loading={statsLoading}
//             iconBg="#E3F1FE"
//             icon={<Cloud size={18} className="text-blue-600" />}
//           />
//           <StatCard
//             label="Total Vehicles Registered"
//             value={stats.totalVehicles?.toLocaleString() ?? "—"}
//             loading={statsLoading}
//             iconBg="#F3E8FD"
//             icon={<Car size={18} className="text-purple-600" />}
//           />
//         </div>

//         {/* Charts row */}
//         <div className="grid grid-cols-12 gap-5 mb-6">
//           {/* Trend chart */}
//           <div className="col-span-5 bg-white rounded-2xl border border-gray-100 p-5 shadow-sm">
//             <div className="flex items-center justify-between mb-4">
//               <h3 className="font-semibold text-gray-900 flex items-center gap-1">
//                 Certificates Trend <span className="text-gray-300 text-xs">ⓘ</span>
//               </h3>
//               <button className="flex items-center gap-1 text-xs border border-gray-200 rounded-lg px-3 py-1.5 text-gray-600">
//                 {range} <ChevronDown size={12} />
//               </button>
//             </div>
//             {trendLoading ? (
//               <div className="h-[260px] flex items-center justify-center text-sm text-gray-400">
//                 Loading trend…
//               </div>
//             ) : trendData.length === 0 ? (
//               <div className="h-[260px] flex items-center justify-center text-sm text-gray-400">
//                 No certificate data yet
//               </div>
//             ) : (
//               <ResponsiveContainer width="100%" height={260}>
//                 <AreaChart data={trendData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
//                   <defs>
//                     <linearGradient id="trendFill" x1="0" y1="0" x2="0" y2="1">
//                       <stop offset="0%" stopColor="#2E7D32" stopOpacity={0.35} />
//                       <stop offset="100%" stopColor="#2E7D32" stopOpacity={0} />
//                     </linearGradient>
//                   </defs>
//                   <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#F0F0F0" />
//                   <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#9AA0A6" }} axisLine={false} tickLine={false} />
//                   <YAxis
//                     tick={{ fontSize: 11, fill: "#9AA0A6" }}
//                     axisLine={false}
//                     tickLine={false}
//                     tickFormatter={(v) => (v >= 1000 ? `${v / 1000}K` : v)}
//                   />
//                   <Tooltip content={<CustomTooltip />} />
//                   <Area
//                     type="monotone"
//                     dataKey="certificates"
//                     stroke="#2E7D32"
//                     strokeWidth={2.5}
//                     fill="url(#trendFill)"
//                     dot={{ r: 3, fill: "#2E7D32", strokeWidth: 0 }}
//                     activeDot={{ r: 5 }}
//                   />
//                 </AreaChart>
//               </ResponsiveContainer>
//             )}
//           </div>

//           {/* Vehicle category donut */}
//           <div className="col-span-4 bg-white rounded-2xl border border-gray-100 p-5 shadow-sm">
//             <div className="flex items-center justify-between mb-2">
//               <h3 className="font-semibold text-gray-900">Vehicle Category</h3>
//               <span className="text-xs text-gray-400">Total Certificates</span>
//             </div>
//             <div className="relative flex items-center justify-center h-44">
//               <ResponsiveContainer width="100%" height="100%">
//                 <PieChart>
//                   <Pie
//                     data={vehicleCategoryData}
//                     dataKey="value"
//                     innerRadius={55}
//                     outerRadius={80}
//                     paddingAngle={2}
//                     stroke="none"
//                   >
//                     {vehicleCategoryData.map((entry, index) => (
//                       <Cell key={index} fill={entry.color} />
//                     ))}
//                   </Pie>
//                 </PieChart>
//               </ResponsiveContainer>
//               <div className="absolute flex flex-col items-center">
//                 <span className="text-xl font-bold text-gray-900">
//                   {stats.totalVehicles?.toLocaleString() ?? "—"}
//                 </span>
//                 <span className="text-xs text-gray-400">Total</span>
//               </div>
//             </div>
//             <div className="space-y-2.5 mt-3">
//               {[
//                 { icon: Car, label: "Cars", value: "45%", color: "#1E6FD9" },
//                 { icon: Bike, label: "Bikes", value: "30%", color: "#F5A623" },
//                 { icon: Truck, label: "Trucks", value: "15%", color: "#2E7D32" },
//                 { icon: MoreHorizontal, label: "Others", value: "10%", color: "#9AA0A6" },
//               ].map((row) => (
//                 <div key={row.label} className="flex items-center justify-between text-sm">
//                   <span className="flex items-center gap-2 text-gray-600">
//                     <row.icon size={15} style={{ color: row.color }} />
//                     {row.label}
//                   </span>
//                   <span className="font-semibold text-gray-800">{row.value}</span>
//                 </div>
//               ))}
//             </div>
//           </div>

//           {/* Carbon footprint impact */}
//           <div className="col-span-3 bg-white rounded-2xl border border-gray-100 p-5 shadow-sm">
//             <h3 className="font-semibold text-gray-900 mb-4">Carbon Footprint Impact</h3>
//             <div className="grid grid-cols-2 gap-3">
//               <div className="bg-emerald-50 rounded-xl p-3">
//                 <Cloud size={16} className="text-emerald-700 mb-2" />
//                 <p className="text-sm font-bold text-gray-900">
//                   {statsLoading ? "…" : `${formattedCarbon} Kg`}
//                 </p>
//                 <p className="text-xs text-gray-500">Total CO₂ Saved</p>
//               </div>
//               <div className="bg-emerald-50 rounded-xl p-3">
//                 <TreePine size={16} className="text-emerald-700 mb-2" />
//                 <p className="text-sm font-bold text-gray-900">
//                   {statsLoading ? "…" : stats.totalVehicles?.toLocaleString()}
//                 </p>
//                 <p className="text-xs text-gray-500">Total Vehicles Registered</p>
//               </div>
//               <div className="bg-emerald-50 rounded-xl p-3">
//                 <Leaf size={16} className="text-emerald-700 mb-2" />
//                 <p className="text-sm font-bold text-gray-900">
//                   {statsLoading || !stats.totalCertificates
//                     ? "…"
//                     : (
//                         stats.totalCarbonFootprint / stats.totalCertificates
//                       ).toFixed(2)}{" "}
//                   Kg
//                 </p>
//                 <p className="text-xs text-gray-500">Avg CO₂ Saved per Certificate</p>
//               </div>
//               <div className="bg-emerald-50 rounded-xl p-3">
//                 <BarChart2 size={16} className="text-emerald-700 mb-2" />
//                 <p className="text-sm font-bold text-gray-900">
//                   {statsLoading ? "…" : stats.totalCertificates?.toLocaleString()}
//                 </p>
//                 <p className="text-xs text-gray-500">Total Certificates Generated</p>
//               </div>
//             </div>
//           </div>
//         </div>

//         {/* Banner */}
//         <div className="bg-emerald-50 rounded-2xl px-6 py-5 mb-6 flex items-center justify-between flex-wrap gap-4">
//           <div className="flex items-center gap-4 max-w-md">
//             <div className="w-14 h-14 rounded-full bg-white flex items-center justify-center shrink-0">
//               <Leaf size={26} className="text-emerald-700" />
//             </div>
//             <div>
//               <p className="font-semibold text-emerald-800">Saving Today, Securing Tomorrow</p>
//               <p className="text-sm text-gray-500">
//                 Your contribution is helping to reduce carbon emissions and build a greener future for everyone.
//               </p>
//             </div>
//           </div>

//           <div className="flex items-center gap-8">
//             {[
//               { icon: Car, value: "578,403", label: "Car Certificates", color: "#2E7D32" },
//               { icon: Bike, value: "385,602", label: "Bike Certificates", color: "#2E7D32" },
//               { icon: Truck, value: "192,801", label: "Truck Certificates", color: "#F5A623" },
//               { icon: MoreHorizontal, value: "128,534", label: "Other Certificates", color: "#9AA0A6" },
//             ].map((item) => (
//               <div key={item.label} className="flex items-center gap-2">
//                 <div
//                   className="w-9 h-9 rounded-full flex items-center justify-center"
//                   style={{ backgroundColor: `${item.color}1A` }}
//                 >
//                   <item.icon size={16} style={{ color: item.color }} />
//                 </div>
//                 <div>
//                   <p className="font-bold text-gray-900 text-sm">{item.value}</p>
//                   <p className="text-xs text-gray-500">{item.label}</p>
//                 </div>
//               </div>
//             ))}
//           </div>
//         </div>

//         {/* Recent certificates table */}
//         <div className="bg-white rounded-2xl border border-gray-100 shadow-sm">
//           <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
//             <h3 className="font-semibold text-gray-900">Recent Green Certificates</h3>
//             <button className="text-xs font-medium border border-gray-200 rounded-lg px-3 py-1.5 text-gray-600">
//               View All
//             </button>
//           </div>

//           <div className="overflow-x-auto">
//             <table className="w-full text-sm">
//               <thead>
//                 <tr className="text-left text-gray-400 text-xs">
//                   <th className="px-6 py-3 font-medium">Certificate No.</th>
//                   <th className="px-4 py-3 font-medium">Vehicle No.</th>
//                   <th className="px-4 py-3 font-medium">Fuel Type</th>
//                   <th className="px-4 py-3 font-medium">Owner Name</th>
//                   <th className="px-4 py-3 font-medium">CO₂ Emission (kg)</th>
//                   <th className="px-4 py-3 font-medium">Distance (km)</th>
//                   <th className="px-4 py-3 font-medium">Issue Date</th>
//                   <th className="px-4 py-3 font-medium">Status</th>
//                   <th className="px-6 py-3 font-medium text-right">Action</th>
//                 </tr>
//               </thead>
//               <tbody>
//                 {recentLoading ? (
//                   <tr>
//                     <td colSpan={9} className="px-6 py-8 text-center text-gray-400">
//                       Loading recent certificates…
//                     </td>
//                   </tr>
//                 ) : recentCertificates.length === 0 ? (
//                   <tr>
//                     <td colSpan={9} className="px-6 py-8 text-center text-gray-400">
//                       No certificates found
//                     </td>
//                   </tr>
//                 ) : (
//                   recentCertificates.map((row, i) => (
//                     <tr
//                       key={row.certificateNumber || i}
//                       className={i !== recentCertificates.length - 1 ? "border-b border-gray-50" : ""}
//                     >
//                       <td className="px-6 py-4 font-medium text-gray-800">
//                         {row.certificateNumber || "—"}
//                       </td>
//                       <td className="px-4 py-4 text-gray-600">{row.vehicleNumber}</td>
//                       <td className="px-4 py-4 text-gray-700">{row.fuelType || "—"}</td>
//                       <td className="px-4 py-4 text-gray-700">{row.ownerName}</td>
//                       <td className="px-4 py-4 text-gray-700">
//                         {typeof row.carbonFootprint === "number"
//                           ? row.carbonFootprint.toFixed(2)
//                           : "—"}
//                       </td>
//                       <td className="px-4 py-4 text-gray-700">{row.distance ?? "—"}</td>
//                       <td className="px-4 py-4 text-gray-500">
//                         {row.issueDate
//                           ? new Date(row.issueDate).toLocaleDateString()
//                           : "—"}
//                       </td>
//                       <td className="px-4 py-4">
//                         <StatusPill status={row.status} />
//                       </td>
//                       <td className="px-6 py-4">
//                         <div className="flex items-center justify-end gap-3 text-gray-400">
//                           {row.pdfUrl && (
//                             <>
//                               <a
//                                 href={row.pdfUrl}
//                                 target="_blank"
//                                 rel="noreferrer"
//                                 title="View certificate"
//                               >
//                                 <Eye size={16} className="cursor-pointer hover:text-gray-600" />
//                               </a>
//                               <a href={row.pdfUrl} download title="Download certificate">
//                                 <Download size={16} className="cursor-pointer hover:text-gray-600" />
//                               </a>
//                             </>
//                           )}
//                           <MoreVertical size={16} className="cursor-pointer hover:text-gray-600" />
//                         </div>
//                       </td>
//                     </tr>
//                   ))
//                 )}
//               </tbody>
//             </table>
//           </div>
//         </div>

//         {/* Footer */}
//         <div className="flex items-center justify-between text-xs text-gray-400 mt-6 pb-2">
//           <span>© 2025 Pure Prakruti Green Certificate System. All rights reserved.</span>
//           <span className="flex items-center gap-1">
//             Every Certificate Counts. Every Act Matters. <Leaf size={12} className="text-emerald-500" />
//           </span>
//         </div>
//       </main>
//     </div>
//   );
// }

// import React, { useState, useEffect, useContext, useRef } from "react";
// import { useNavigate } from "react-router-dom";
// import {
//   AreaChart,
//   Area,
//   XAxis,
//   YAxis,
//   CartesianGrid,
//   Tooltip,
//   ResponsiveContainer,
//   PieChart,
//   Pie,
//   Cell,
// } from "recharts";
// import {
//   LayoutDashboard,
//   FileText,
//   Car,
//   Users,
//   BarChart2,
//   Leaf,
//   Download,
//   Settings,
//   LogOut,
//   Menu,
//   Bell,
//   ChevronDown,
//   Calendar,
//   Bike,
//   Truck,
//   MoreHorizontal,
//   Eye,
//   MoreVertical,
//   TreePine,
//   Cloud,
//   User,
// } from "lucide-react";
// import { AuthContext } from "../AuthContext";
// import API_BASE_URL from "../api"; // ✅ same pattern as UserDashBoard.jsx

// // ---------- Config ----------

// const API_BASE = `${API_BASE_URL}/api/inputHistory`;

// // ---------- Mock data (kept only where no API exists yet) ----------

// const trendData = [
//   { month: "Jan", certificates: 42000 },
//   { month: "Feb", certificates: 58000 },
//   { month: "Mar", certificates: 65000 },
//   { month: "Apr", certificates: 88000 },
//   { month: "May", certificates: 79000 },
//   { month: "Jun", certificates: 92000 },
//   { month: "Jul", certificates: 86000 },
//   { month: "Aug", certificates: 128450 },
//   { month: "Sep", certificates: 108000 },
//   { month: "Oct", certificates: 118000 },
//   { month: "Nov", certificates: 122000 },
//   { month: "Dec", certificates: 168000 },
// ];

// const vehicleCategoryData = [
//   { name: "Cars", value: 45, color: "#2E7D32" },
//   { name: "Bikes", value: 30, color: "#F5A623" },
//   { name: "Trucks", value: 15, color: "#1E6FD9" },
//   { name: "Others", value: 10, color: "#9AA0A6" },
// ];

// const recentCertificates = [
//   {
//     id: "CERT-9ccaea75",
//     vehicleNo: "BR01GB5618",
//     type: "Car",
//     icon: Car,
//     iconColor: "#1E6FD9",
//     owner: "Jagdish",
//     co2: "0.6",
//     trees: 8,
//     date: "Dec 26, 2025",
//     status: "Verified",
//   },
//   {
//     id: "CERT-9ccaea76",
//     vehicleNo: "BR01HY9876",
//     type: "Bike",
//     icon: Bike,
//     iconColor: "#1E6FD9",
//     owner: "Rahul Kumar",
//     co2: "0.3",
//     trees: 5,
//     date: "Dec 26, 2025",
//     status: "Verified",
//   },
//   {
//     id: "CERT-9ccaea77",
//     vehicleNo: "BR01GT2233",
//     type: "Truck",
//     icon: Truck,
//     iconColor: "#F5A623",
//     owner: "Amit Sharma",
//     co2: "1.2",
//     trees: 15,
//     date: "Dec 26, 2025",
//     status: "Pending",
//   },
//   {
//     id: "CERT-9ccaea78",
//     vehicleNo: "BR01ZZ4455",
//     type: "Other",
//     icon: MoreHorizontal,
//     iconColor: "#9AA0A6",
//     owner: "Neha Singh",
//     co2: "0.2",
//     trees: 3,
//     date: "Dec 26, 2025",
//     status: "Verified",
//   },
// ];

// // ---------- Small building blocks ----------

// function NavItem({ icon: Icon, label, active }) {
//   return (
//     <div
//       className={`flex items-center gap-3 px-4 py-2.5 rounded-lg cursor-pointer transition-colors text-sm ${
//         active
//           ? "bg-white text-emerald-800 font-semibold"
//           : "text-emerald-50/80 hover:bg-emerald-800/60"
//       }`}
//     >
//       <Icon size={18} />
//       <span>{label}</span>
//     </div>
//   );
// }

// function StatCard({ label, value, unit, change, icon, iconBg, loading }) {
//   return (
//     <div className="bg-white rounded-2xl border border-gray-100 p-5 flex flex-col gap-2 shadow-sm">
//       <div className="flex items-start justify-between">
//         <span className="text-sm text-gray-500 flex items-center gap-1">
//           {label}
//           <span className="text-gray-300 text-xs">ⓘ</span>
//         </span>
//         <div
//           className="w-10 h-10 rounded-full flex items-center justify-center shrink-0"
//           style={{ backgroundColor: iconBg }}
//         >
//           {icon}
//         </div>
//       </div>
//       <div className="flex items-baseline gap-2">
//         {loading ? (
//           <span className="h-7 w-24 bg-gray-100 rounded animate-pulse" />
//         ) : (
//           <span className="text-2xl font-bold text-gray-900">{value}</span>
//         )}
//         {unit && !loading && <span className="text-sm text-gray-400">{unit}</span>}
//       </div>
//       {change && (
//         <span className="text-xs font-medium text-emerald-600">
//           ↑ {change} vs last {label.toLowerCase().includes("today") ? "yesterday" : "month"}
//         </span>
//       )}
//     </div>
//   );
// }

// function StatusPill({ status }) {
//   const isVerified = status === "Verified";
//   return (
//     <span
//       className={`inline-flex items-center gap-1 text-xs font-medium ${
//         isVerified ? "text-emerald-600" : "text-amber-500"
//       }`}
//     >
//       <span className="text-[8px]">●</span>
//       {status}
//     </span>
//   );
// }

// function CustomTooltip({ active, payload, label }) {
//   if (active && payload && payload.length) {
//     return (
//       <div className="bg-white border border-gray-100 shadow-lg rounded-xl px-4 py-3">
//         <p className="text-xs text-gray-400 mb-1">{label} 2025</p>
//         <p className="text-sm font-semibold text-emerald-700">
//           {payload[0].value.toLocaleString()} Certificates
//         </p>
//       </div>
//     );
//   }
//   return null;
// }

// // ---------- Main Component ----------

// export default function AdminDashboard() {
//   const [range] = useState("This Year");
//   const navigate = useNavigate();

//   // ✅ Same AuthContext pattern used in UserDashBoard.jsx
//   const { user, setUser } = useContext(AuthContext);

//   const [profileOpen, setProfileOpen] = useState(false);
//   const profileRef = useRef(null);

//   // ---------- Live dashboard stats ----------
//   const [stats, setStats] = useState({
//     totalCertificates: null,
//     totalVehicles: null,
//     totalCarbonFootprint: null,
//   });
//   const [statsLoading, setStatsLoading] = useState(true);
//   const [statsError, setStatsError] = useState(null);

//   useEffect(() => {
//     const fetchStats = async () => {
//       setStatsLoading(true);
//       setStatsError(null);
//       try {
//         const [certRes, vehicleRes, carbonRes] = await Promise.all([
//           fetch(`${API_BASE}/totalCertificateGenerated`).then((r) => r.json()),
//           fetch(`${API_BASE}/totalVehicleRegistered`).then((r) => r.json()),
//           fetch(`${API_BASE}/totalCarbonFootprintGenerated`).then((r) => r.json()),
//         ]);

//         setStats({
//           totalCertificates: certRes?.totalCertificate ?? 0,
//           totalVehicles: vehicleRes?.totalVehicleRegistered ?? 0,
//           totalCarbonFootprint: carbonRes?.totalCarbonFootprintGenerated ?? 0,
//         });
//       } catch (err) {
//         console.error("Failed to load dashboard stats:", err);
//         setStatsError("Couldn't load live stats. Showing last known values.");
//       } finally {
//         setStatsLoading(false);
//       }
//     };

//     fetchStats();
//   }, []);

//   // Close profile dropdown on outside click
//   useEffect(() => {
//     const handleClickOutside = (e) => {
//       if (profileRef.current && !profileRef.current.contains(e.target)) {
//         setProfileOpen(false);
//       }
//     };
//     document.addEventListener("mousedown", handleClickOutside);
//     return () => document.removeEventListener("mousedown", handleClickOutside);
//   }, []);

//   const handleLogout = () => {
//     // Clear auth state the same way it's set on login
//     setUser(null);
//     localStorage.removeItem("token");
//     localStorage.removeItem("user");
//     navigate("/login");
//   };

//   const formattedCarbon =
//     stats.totalCarbonFootprint !== null
//       ? Number(stats.totalCarbonFootprint).toLocaleString(undefined, {
//           maximumFractionDigits: 2,
//         })
//       : "—";

//   return (
//     <div className="min-h-screen w-full flex bg-gray-50 font-sans text-gray-900">
//       {/* Sidebar */}
//       <aside className="w-64 bg-[#0E3B2E] flex flex-col shrink-0">
//         <div className="flex flex-col items-center text-center px-6 pt-8 pb-6 border-b border-emerald-800/60">
//           <div className="w-16 h-16 rounded-full bg-white flex items-center justify-center mb-3">
//             <Leaf className="text-emerald-700" size={28} />
//           </div>
//           <h1 className="text-white font-bold text-lg leading-tight">PURE PRAKRUTI</h1>
//           <p className="text-emerald-200/70 text-xs mt-1">Green Certificate System</p>
//         </div>

//         <nav className="flex-1 px-3 py-4 space-y-1">
//           <NavItem icon={LayoutDashboard} label="Dashboard" active />
//           <NavItem icon={FileText} label="Green Certificates" />
//           <NavItem icon={Car} label="Vehicles" />
//           <NavItem icon={Users} label="Users" />
//           <NavItem icon={BarChart2} label="Reports" />
//           <NavItem icon={Leaf} label="Environmental Impact" />
//           <NavItem icon={Download} label="Download Center" />
//           <NavItem icon={Settings} label="Settings" />
//         </nav>

//         <div className="mx-3 mb-4 bg-emerald-800/40 rounded-2xl p-4 text-emerald-50">
//           <p className="text-sm font-semibold leading-snug mb-3">
//             Together we can make our planet greener
//           </p>
//           <div className="w-full h-20 rounded-xl bg-emerald-900/40 flex items-center justify-center">
//             <Leaf size={32} className="text-emerald-300" />
//           </div>
//         </div>

//         <div className="px-6 pb-6">
//           <button
//             onClick={handleLogout}
//             className="flex items-center gap-2 text-emerald-100 text-sm hover:text-white"
//           >
//             <LogOut size={16} />
//             Logout
//           </button>
//         </div>
//       </aside>

//       {/* Main content */}
//       <main className="flex-1 px-8 py-6 overflow-y-auto">
//         {/* Header */}
//         <div className="flex items-center justify-between mb-6">
//           <div className="flex items-center gap-4">
//             <Menu className="text-gray-500" size={22} />
//             <div>
//               <h2 className="text-xl font-bold text-gray-900">Dashboard</h2>
//               <p className="text-sm text-gray-500">
//                 Welcome back, {user?.userName || "Admin"} 👋
//               </p>
//             </div>
//           </div>

//           <div className="flex items-center gap-4">
//             <button className="flex items-center gap-2 border border-gray-200 rounded-xl px-4 py-2 text-sm text-gray-600 bg-white">
//               <Calendar size={16} />
//               Dec 20, 2025 - Dec 26, 2025
//               <ChevronDown size={14} />
//             </button>
//             <div className="relative">
//               <Bell className="text-gray-500" size={20} />
//               <span className="absolute -top-1 -right-1 bg-emerald-600 text-white text-[10px] rounded-full w-4 h-4 flex items-center justify-center">
//                 5
//               </span>
//             </div>

//             {/* Profile dropdown */}
//             <div className="relative" ref={profileRef}>
//               <button
//                 onClick={() => setProfileOpen((prev) => !prev)}
//                 className="flex items-center gap-2"
//               >
//                 {user?.avatar ? (
//                   <img
//                     src={user.avatar}
//                     alt="Admin avatar"
//                     className="w-9 h-9 rounded-full object-cover"
//                   />
//                 ) : (
//                   <div className="w-9 h-9 rounded-full bg-emerald-100 flex items-center justify-center">
//                     <User size={18} className="text-emerald-700" />
//                   </div>
//                 )}
//                 <div className="text-sm text-left">
//                   <p className="font-semibold leading-tight">{user?.userName || "Admin"}</p>
//                   <p className="text-gray-400 text-xs leading-tight">
//                     {user?.role || "Super Admin"}
//                   </p>
//                 </div>
//                 <ChevronDown size={14} className="text-gray-400" />
//               </button>

//               {profileOpen && (
//                 <div className="absolute right-0 mt-2 w-44 bg-white border border-gray-100 rounded-xl shadow-lg py-2 z-10">
//                   <button
//                     onClick={() => navigate("/settings")}
//                     className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-600 hover:bg-gray-50"
//                   >
//                     <Settings size={15} /> Settings
//                   </button>
//                   <button
//                     onClick={handleLogout}
//                     className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-500 hover:bg-red-50"
//                   >
//                     <LogOut size={15} /> Logout
//                   </button>
//                 </div>
//               )}
//             </div>
//           </div>
//         </div>

//         {statsError && (
//           <div className="mb-4 text-xs text-amber-600 bg-amber-50 border border-amber-100 rounded-lg px-4 py-2">
//             {statsError}
//           </div>
//         )}

//         {/* Stat cards — now driven by live API data */}
//         <div className="grid grid-cols-3 gap-5 mb-6">
//           <StatCard
//             label="Total Green Certificates"
//             value={stats.totalCertificates?.toLocaleString() ?? "—"}
//             loading={statsLoading}
//             iconBg="#E8F5E9"
//             icon={<FileText size={18} className="text-emerald-700" />}
//           />
//           <StatCard
//             label="Total CO₂ Generated"
//             value={formattedCarbon}
//             unit="Kg"
//             loading={statsLoading}
//             iconBg="#E3F1FE"
//             icon={<Cloud size={18} className="text-blue-600" />}
//           />
//           <StatCard
//             label="Total Vehicles Registered"
//             value={stats.totalVehicles?.toLocaleString() ?? "—"}
//             loading={statsLoading}
//             iconBg="#F3E8FD"
//             icon={<Car size={18} className="text-purple-600" />}
//           />
//         </div>

//         {/* Charts row */}
//         <div className="grid grid-cols-12 gap-5 mb-6">
//           {/* Trend chart */}
//           <div className="col-span-5 bg-white rounded-2xl border border-gray-100 p-5 shadow-sm">
//             <div className="flex items-center justify-between mb-4">
//               <h3 className="font-semibold text-gray-900 flex items-center gap-1">
//                 Certificates Trend <span className="text-gray-300 text-xs">ⓘ</span>
//               </h3>
//               <button className="flex items-center gap-1 text-xs border border-gray-200 rounded-lg px-3 py-1.5 text-gray-600">
//                 {range} <ChevronDown size={12} />
//               </button>
//             </div>
//             <ResponsiveContainer width="100%" height={260}>
//               <AreaChart data={trendData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
//                 <defs>
//                   <linearGradient id="trendFill" x1="0" y1="0" x2="0" y2="1">
//                     <stop offset="0%" stopColor="#2E7D32" stopOpacity={0.35} />
//                     <stop offset="100%" stopColor="#2E7D32" stopOpacity={0} />
//                   </linearGradient>
//                 </defs>
//                 <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#F0F0F0" />
//                 <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#9AA0A6" }} axisLine={false} tickLine={false} />
//                 <YAxis
//                   tick={{ fontSize: 11, fill: "#9AA0A6" }}
//                   axisLine={false}
//                   tickLine={false}
//                   tickFormatter={(v) => (v >= 1000 ? `${v / 1000}K` : v)}
//                 />
//                 <Tooltip content={<CustomTooltip />} />
//                 <Area
//                   type="monotone"
//                   dataKey="certificates"
//                   stroke="#2E7D32"
//                   strokeWidth={2.5}
//                   fill="url(#trendFill)"
//                   dot={{ r: 3, fill: "#2E7D32", strokeWidth: 0 }}
//                   activeDot={{ r: 5 }}
//                 />
//               </AreaChart>
//             </ResponsiveContainer>
//           </div>

//           {/* Vehicle category donut */}
//           <div className="col-span-4 bg-white rounded-2xl border border-gray-100 p-5 shadow-sm">
//             <div className="flex items-center justify-between mb-2">
//               <h3 className="font-semibold text-gray-900">Vehicle Category</h3>
//               <span className="text-xs text-gray-400">Total Certificates</span>
//             </div>
//             <div className="relative flex items-center justify-center h-44">
//               <ResponsiveContainer width="100%" height="100%">
//                 <PieChart>
//                   <Pie
//                     data={vehicleCategoryData}
//                     dataKey="value"
//                     innerRadius={55}
//                     outerRadius={80}
//                     paddingAngle={2}
//                     stroke="none"
//                   >
//                     {vehicleCategoryData.map((entry, index) => (
//                       <Cell key={index} fill={entry.color} />
//                     ))}
//                   </Pie>
//                 </PieChart>
//               </ResponsiveContainer>
//               <div className="absolute flex flex-col items-center">
//                 <span className="text-xl font-bold text-gray-900">
//                   {stats.totalVehicles?.toLocaleString() ?? "—"}
//                 </span>
//                 <span className="text-xs text-gray-400">Total</span>
//               </div>
//             </div>
//             <div className="space-y-2.5 mt-3">
//               {[
//                 { icon: Car, label: "Cars", value: "45%", color: "#1E6FD9" },
//                 { icon: Bike, label: "Bikes", value: "30%", color: "#F5A623" },
//                 { icon: Truck, label: "Trucks", value: "15%", color: "#2E7D32" },
//                 { icon: MoreHorizontal, label: "Others", value: "10%", color: "#9AA0A6" },
//               ].map((row) => (
//                 <div key={row.label} className="flex items-center justify-between text-sm">
//                   <span className="flex items-center gap-2 text-gray-600">
//                     <row.icon size={15} style={{ color: row.color }} />
//                     {row.label}
//                   </span>
//                   <span className="font-semibold text-gray-800">{row.value}</span>
//                 </div>
//               ))}
//             </div>
//           </div>

//           {/* Carbon footprint impact */}
//           <div className="col-span-3 bg-white rounded-2xl border border-gray-100 p-5 shadow-sm">
//             <h3 className="font-semibold text-gray-900 mb-4">Carbon Footprint Impact</h3>
//             <div className="grid grid-cols-2 gap-3">
//               <div className="bg-emerald-50 rounded-xl p-3">
//                 <Cloud size={16} className="text-emerald-700 mb-2" />
//                 <p className="text-sm font-bold text-gray-900">
//                   {statsLoading ? "…" : `${formattedCarbon} Kg`}
//                 </p>
//                 <p className="text-xs text-gray-500">Total CO₂ Generated</p>
//               </div>
//               <div className="bg-emerald-50 rounded-xl p-3">
//                 <TreePine size={16} className="text-emerald-700 mb-2" />
//                 <p className="text-sm font-bold text-gray-900">
//                   {statsLoading ? "…" : stats.totalVehicles?.toLocaleString()}
//                 </p>
//                 <p className="text-xs text-gray-500">Total Vehicles Registered</p>
//               </div>
//               <div className="bg-emerald-50 rounded-xl p-3">
//                 <Leaf size={16} className="text-emerald-700 mb-2" />
//                 <p className="text-sm font-bold text-gray-900">
//                   {statsLoading || !stats.totalCertificates
//                     ? "…"
//                     : (
//                         stats.totalCarbonFootprint / stats.totalCertificates
//                       ).toFixed(2)}{" "}
//                   Kg
//                 </p>
//                 <p className="text-xs text-gray-500">Avg CO₂ Generated per Certificate</p>
//               </div>
//               <div className="bg-emerald-50 rounded-xl p-3">
//                 <BarChart2 size={16} className="text-emerald-700 mb-2" />
//                 <p className="text-sm font-bold text-gray-900">
//                   {statsLoading ? "…" : stats.totalCertificates?.toLocaleString()}
//                 </p>
//                 <p className="text-xs text-gray-500">Total Certificates Generated</p>
//               </div>
//             </div>
//           </div>
//         </div>

//         {/* Banner */}
//         <div className="bg-emerald-50 rounded-2xl px-6 py-5 mb-6 flex items-center justify-between flex-wrap gap-4">
//           <div className="flex items-center gap-4 max-w-md">
//             <div className="w-14 h-14 rounded-full bg-white flex items-center justify-center shrink-0">
//               <Leaf size={26} className="text-emerald-700" />
//             </div>
//             <div>
//               <p className="font-semibold text-emerald-800">Saving Today, Securing Tomorrow</p>
//               <p className="text-sm text-gray-500">
//                 Your contribution is helping to reduce carbon emissions and build a greener future for everyone.
//               </p>
//             </div>
//           </div>

//           <div className="flex items-center gap-8">
//             {[
//               { icon: Car, value: "578,403", label: "Car Certificates", color: "#2E7D32" },
//               { icon: Bike, value: "385,602", label: "Bike Certificates", color: "#2E7D32" },
//               { icon: Truck, value: "192,801", label: "Truck Certificates", color: "#F5A623" },
//               { icon: MoreHorizontal, value: "128,534", label: "Other Certificates", color: "#9AA0A6" },
//             ].map((item) => (
//               <div key={item.label} className="flex items-center gap-2">
//                 <div
//                   className="w-9 h-9 rounded-full flex items-center justify-center"
//                   style={{ backgroundColor: `${item.color}1A` }}
//                 >
//                   <item.icon size={16} style={{ color: item.color }} />
//                 </div>
//                 <div>
//                   <p className="font-bold text-gray-900 text-sm">{item.value}</p>
//                   <p className="text-xs text-gray-500">{item.label}</p>
//                 </div>
//               </div>
//             ))}
//           </div>
//         </div>

//         {/* Recent certificates table */}
//         <div className="bg-white rounded-2xl border border-gray-100 shadow-sm">
//           <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
//             <h3 className="font-semibold text-gray-900">Recent Green Certificates</h3>
//             <button className="text-xs font-medium border border-gray-200 rounded-lg px-3 py-1.5 text-gray-600">
//               View All
//             </button>
//           </div>

//           <div className="overflow-x-auto">
//             <table className="w-full text-sm">
//               <thead>
//                 <tr className="text-left text-gray-400 text-xs">
//                   <th className="px-6 py-3 font-medium">Certificate No.</th>
//                   <th className="px-4 py-3 font-medium">Vehicle No.</th>
//                   <th className="px-4 py-3 font-medium">Vehicle Type</th>
//                   <th className="px-4 py-3 font-medium">Owner Name</th>
//                   <th className="px-4 py-3 font-medium">CO₂ Generated (Unit)</th>
//                   <th className="px-4 py-3 font-medium">Trees Recommended</th>
//                   <th className="px-4 py-3 font-medium">Issue Date</th>
//                   <th className="px-4 py-3 font-medium">Status</th>
//                   <th className="px-6 py-3 font-medium text-right">Action</th>
//                 </tr>
//               </thead>
//               <tbody>
//                 {recentCertificates.map((row, i) => (
//                   <tr
//                     key={row.id}
//                     className={i !== recentCertificates.length - 1 ? "border-b border-gray-50" : ""}
//                   >
//                     <td className="px-6 py-4 font-medium text-gray-800">{row.id}</td>
//                     <td className="px-4 py-4 text-gray-600">{row.vehicleNo}</td>
//                     <td className="px-4 py-4">
//                       <span className="flex items-center gap-2 text-gray-700">
//                         <row.icon size={16} style={{ color: row.iconColor }} />
//                         {row.type}
//                       </span>
//                     </td>
//                     <td className="px-4 py-4 text-gray-700">{row.owner}</td>
//                     <td className="px-4 py-4 text-gray-700">{row.co2}</td>
//                     <td className="px-4 py-4">
//                       <span className="flex items-center gap-1 text-gray-700">
//                         {row.trees} <TreePine size={14} className="text-emerald-600" />
//                       </span>
//                     </td>
//                     <td className="px-4 py-4 text-gray-500">{row.date}</td>
//                     <td className="px-4 py-4">
//                       <StatusPill status={row.status} />
//                     </td>
//                     <td className="px-6 py-4">
//                       <div className="flex items-center justify-end gap-3 text-gray-400">
//                         <Eye size={16} className="cursor-pointer hover:text-gray-600" />
//                         <Download size={16} className="cursor-pointer hover:text-gray-600" />
//                         <MoreVertical size={16} className="cursor-pointer hover:text-gray-600" />
//                       </div>
//                     </td>
//                   </tr>
//                 ))}
//               </tbody>
//             </table>
//           </div>
//         </div>

//         {/* Footer */}
//         <div className="flex items-center justify-between text-xs text-gray-400 mt-6 pb-2">
//           <span>© 2025 Pure Prakruti Green Certificate System. All rights reserved.</span>
//           <span className="flex items-center gap-1">
//             Every Certificate Counts. Every Act Matters. <Leaf size={12} className="text-emerald-500" />
//           </span>
//         </div>
//       </main>
//     </div>
//   );
// }
// import React, { useEffect, useState, useContext } from "react";
// import { AuthContext } from "../AuthContext";
// import axios from "axios";
// import API_BASE_URL from "../api";
// import { Users, Truck, Leaf, ShieldCheck, Search } from "lucide-react";
// import natureImg from "../resource/natureImg.png";

// export default function AdminDashboard() {
//   const { user } = useContext(AuthContext);

//   const [stats, setStats] = useState({
//     totalUsers: 0,
//     totalVehicles: 0,
//     totalEmission: 0,
//     pendingKyc: 0,
//   });
//   const [users, setUsers] = useState([]);
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState("");
//   const [search, setSearch] = useState("");

//   useEffect(() => {
//     const fetchAdminData = async () => {
//       try {
//         setLoading(true);
//         // NOTE: these are placeholder endpoints — implement them on the
//         // backend (adminController.js) and point this to the real routes.
//         const [statsRes, usersRes] = await Promise.all([
//           axios.get(`${API_BASE_URL}/api/admin/stats`),
//           axios.get(`${API_BASE_URL}/api/admin/users`),
//         ]);

//         setStats(statsRes.data?.stats || {});
//         setUsers(usersRes.data?.users || []);
//       } catch (err) {
//         setError(
//           err.response?.data?.message || "Failed to load admin data"
//         );
//       } finally {
//         setLoading(false);
//       }
//     };

//     fetchAdminData();
//   }, []);

//   const filteredUsers = users.filter((u) =>
//     [u.userName, u.mobileNumber, u.baseUsername]
//       .filter(Boolean)
//       .some((field) => field.toLowerCase().includes(search.toLowerCase()))
//   );

//   return (
//     <div className="w-full min-h-screen relative">
//       <img
//         className="absolute inset-0 w-full h-full object-cover z-0 opacity-90"
//         src="/bglogin3.jpg"
//         alt="Background"
//       />

//       <div
//         style={{ backgroundImage: `url(${natureImg})` }}
//         className="relative z-10 w-full min-h-[100px] bg-cover bg-center bg-no-repeat text-white p-6 flex items-center justify-between"
//       >
//         <div className="flex items-center gap-3">
//           <ShieldCheck className="w-8 h-8" />
//           <h1 className="text-2xl md:text-3xl font-bold">Admin Dashboard</h1>
//         </div>
//         <span className="text-lg opacity-90">
//           Welcome, {user?.userName || "Admin"}
//         </span>
//       </div>

//       <div className="relative z-10 px-4 sm:px-8 md:px-16 lg:px-24 py-8">
//         {error && (
//           <p className="text-red-400 text-center text-lg mb-4">{error}</p>
//         )}

//         {/* Stat cards */}
//         <div className="flex flex-wrap justify-center md:justify-between gap-6 mb-12">
//           <div className="bg-gradient-to-r from-green-600/80 to-green-900 p-6 rounded-xl shadow-lg min-h-[150px] w-full sm:w-[45%] lg:w-1/4 text-center border border-gray-300 hover:shadow-xl transition duration-300 hover:scale-105">
//             <Users className="mx-auto mb-2 text-white" size={28} />
//             <div className="font-bold text-2xl text-white">
//               {stats.totalUsers}
//             </div>
//             <h2 className="text-gray-200 text-lg mt-1">Total Users</h2>
//           </div>

//           <div className="bg-gradient-to-r from-green-700 to-green-900 p-6 rounded-xl shadow-lg min-h-[150px] w-full sm:w-[45%] lg:w-1/4 text-center border border-gray-300 hover:shadow-xl transition duration-300 hover:scale-105">
//             <Truck className="mx-auto mb-2 text-white" size={28} />
//             <div className="font-bold text-2xl text-white">
//               {stats.totalVehicles}
//             </div>
//             <h2 className="text-gray-200 text-lg mt-1">Total Vehicles</h2>
//           </div>

//           <div className="bg-gradient-to-r from-green-700 to-green-900 p-6 rounded-xl shadow-lg min-h-[150px] w-full sm:w-[45%] lg:w-1/4 text-center border border-gray-300 hover:shadow-xl transition duration-300 hover:scale-105">
//             <Leaf className="mx-auto mb-2 text-white" size={28} />
//             <div className="font-bold text-2xl text-white">
//               {Number(stats.totalEmission || 0).toFixed(2)} kg
//             </div>
//             <h2 className="text-gray-200 text-lg mt-1">Total Emission</h2>
//           </div>

//           <div className="bg-gradient-to-r from-green-800 to-green-950 p-6 rounded-xl shadow-lg min-h-[150px] w-full sm:w-[45%] lg:w-1/4 text-center border border-gray-300 hover:shadow-xl transition duration-300 hover:scale-105">
//             <ShieldCheck className="mx-auto mb-2 text-white" size={28} />
//             <div className="font-bold text-2xl text-white">
//               {stats.pendingKyc}
//             </div>
//             <h2 className="text-gray-200 text-lg mt-1">Pending KYC</h2>
//           </div>
//         </div>

//         {/* Users table */}
//         <div className="bg-gradient-to-r from-green-900 to-green-950/60 p-4 rounded-xl shadow-lg">
//           <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 m-4">
//             <h2 className="text-2xl sm:text-3xl font-bold text-white">
//               All Users
//             </h2>
//             <div className="flex items-center bg-green-700/50 rounded-md px-3 py-2 w-full sm:w-72">
//               <Search className="text-white mr-2" size={18} />
//               <input
//                 type="text"
//                 placeholder="Search by name / mobile"
//                 value={search}
//                 onChange={(e) => setSearch(e.target.value)}
//                 className="bg-transparent outline-none text-white placeholder-white/80 w-full"
//               />
//             </div>
//           </div>

//           <div className="overflow-x-auto max-h-128 custom-scrollbar">
//             {loading ? (
//               <p className="text-white text-center p-6">Loading...</p>
//             ) : (
//               <table className="w-full border-collapse border border-gray-300 min-w-[600px]">
//                 <thead>
//                   <tr className="bg-green-700 text-lg text-white">
//                     <th className="border p-3 md:p-4">Name</th>
//                     <th className="border p-3 md:p-4">Mobile Number</th>
//                     <th className="border p-3 md:p-4">Vehicle Type</th>
//                     <th className="border p-3 md:p-4">KYC Status</th>
//                     <th className="border p-3 md:p-4">Joined On</th>
//                   </tr>
//                 </thead>
//                 <tbody>
//                   {filteredUsers.length > 0 ? (
//                     filteredUsers.map((u, idx) => (
//                       <tr
//                         key={u._id || idx}
//                         className="text-center text-base md:text-lg text-white"
//                       >
//                         <td className="border p-3 md:p-4">
//                           {u.userName || "—"}
//                         </td>
//                         <td className="border p-3 md:p-4">
//                           {u.mobileNumber}
//                         </td>
//                         <td className="border p-3 md:p-4">
//                           {u.vehicleType || "—"}
//                         </td>
//                         <td className="border p-3 md:p-4">
//                           <span
//                             className={`px-3 py-1 rounded-full text-sm ${
//                               u.kycStatus === "VERIFIED"
//                                 ? "bg-green-500"
//                                 : u.kycStatus === "PENDING"
//                                 ? "bg-yellow-500"
//                                 : "bg-red-500"
//                             }`}
//                           >
//                             {u.kycStatus || "N/A"}
//                           </span>
//                         </td>
//                         <td className="border p-3 md:p-4">
//                           {u.createdAt
//                             ? new Date(u.createdAt).toLocaleDateString()
//                             : "—"}
//                         </td>
//                       </tr>
//                     ))
//                   ) : (
//                     <tr>
//                       <td
//                         colSpan={5}
//                         className="text-center text-gray-300 p-6"
//                       >
//                         No users found.
//                       </td>
//                     </tr>
//                   )}
//                 </tbody>
//               </table>
//             )}
//           </div>
//         </div>
//       </div>
//     </div>
//   );
// }